import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { I18nProvider } from '@i18n';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { useEffect, useMemo, useState } from 'react';
import { SessionInitializer } from '@/components/session/SessionInitializer';
import { GlobalSearchProvider } from '@/contexts/GlobalSearchContext';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import GeneralErrorBoundary from './components/error-boundaries/GeneralErrorBoundary';
import FeatureFlagPlayground from './components/FeatureFlagPlayground/FeatureFlagPlayground';
import DownloadsProvider from './components/notifications/DownloadsContext';
import NotistackProvider from './components/NotistackProvider';
import { PendingBulkActionsPoller } from './components/PendingBulkActionsPoller/PendingBulkActionsPoller';
import ThemeColorPresets from './components/ThemeColorPresets';
import { OrgPreferencesContextProvider } from './contexts/OrgPreferences/OrgPreferencesContext';
import AuthenticationGuard, { isSharedPage } from './guards/AuthenticationGuard';
import ApiHealthCheckPage from './pages/ApiHealthCheckPage';
import { DatePickerLanguageProvider } from './provider/DatePickerLanguageProvider';
import HasuraApiProvider from './provider/HasuraApiProvider';
import PostHogProviderWithErrorBoundary from './provider/PostHogProviderWithErrorBoundary';
import { Router } from './routes';
import ThemeProvider from './theme';
import { initUpscope } from './utils/upscope';

export default function App() {
  useVersionCheck();

  const [clientCacheAndNetworkPolicy, setClientCacheAndNetworkPolicy] = useState(false);

  useEffect(() => {
    //check if the analytics script is already loaded
    if (document.getElementById('analytics-script')) {
      return;
    }
  }, []);

  useEffect(() => {
    initUpscope();
  }, []);

  // Shared pages use an unauthenticated Apollo Client (no auth headers → Hasura public role).
  const publicApolloClient = useMemo(
    () =>
      new ApolloClient({
        link: new HttpLink({ uri: import.meta.env.VITE_HASURA_ENDPOINT }),
        cache: new InMemoryCache(),
      }),
    [],
  );

  // Shared pages are publicly accessible and don't need auth or session providers
  if (isSharedPage()) {
    return (
      <PostHogProviderWithErrorBoundary>
        <ThemeProvider>
          <I18nProvider>
            <DatePickerLanguageProvider>
              <ThemeColorPresets>
                <GeneralErrorBoundary>
                  <NotistackProvider>
                    <ApolloProvider client={publicApolloClient}>
                      <NuqsAdapter>
                        <Router />
                      </NuqsAdapter>
                    </ApolloProvider>
                  </NotistackProvider>
                </GeneralErrorBoundary>
              </ThemeColorPresets>
            </DatePickerLanguageProvider>
          </I18nProvider>
        </ThemeProvider>
      </PostHogProviderWithErrorBoundary>
    );
  }

  return (
    <PostHogProviderWithErrorBoundary>
      <ThemeProvider>
        <I18nProvider>
          <DatePickerLanguageProvider>
            <ThemeColorPresets>
              <AuthenticationGuard>
                <GeneralErrorBoundary>
                  <NotistackProvider>
                    <HasuraApiProvider clientCacheAndNetworkPolicy={clientCacheAndNetworkPolicy}>
                      <SessionInitializer setClientCacheAndNetworkPolicy={setClientCacheAndNetworkPolicy}>
                        <DownloadsProvider>
                          <ApiHealthCheckPage>
                            <OrgPreferencesContextProvider>
                              <NuqsAdapter>
                                <GlobalSearchProvider>
                                  <Router />
                                </GlobalSearchProvider>
                              </NuqsAdapter>
                              <FeatureFlagPlayground />
                              <PendingBulkActionsPoller />
                            </OrgPreferencesContextProvider>
                          </ApiHealthCheckPage>
                        </DownloadsProvider>
                      </SessionInitializer>
                    </HasuraApiProvider>
                  </NotistackProvider>
                </GeneralErrorBoundary>
              </AuthenticationGuard>
            </ThemeColorPresets>
          </DatePickerLanguageProvider>
        </I18nProvider>
      </ThemeProvider>
    </PostHogProviderWithErrorBoundary>
  );
}
